import React, { useState, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { toE164 } from "../../utils/phone";

const emptyForm = {
  firstname: "",
  lastname: "",
  email: "",
  mobile: "",
  title: "",
  billing_group: false,
  technical_group: false,
  admin_group: false,
};

function generateRandomPassword(length = 40) {
  const characters =
    "abcdefghijkmnpqrtuvwxyzABCDEFGHIJKLMNPQRTUVWXYZ12346789";
  let password = "";
  for (let i = 0; i < length; i += 1) {
    password += characters.charAt(
      Math.floor(Math.random() * characters.length)
    );
  }
  return password;
}

export default function ContactEditorModal({
  isOpen,
  onClose,
  contact,
  accountId,
  onSave,
  defaultValues,
}) {
  const [contactForm, setContactForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      if (contact) {
        setContactForm({
          firstname: contact.firstname || "",
          lastname: contact.lastname || "",
          email: contact.email || "",
          mobile: contact.mobile || contact.phone || "",
          title: contact.title || "",
          billing_group: contact.billing_group || false,
          technical_group: contact.technical_group || false,
          admin_group: contact.admin_group || false,
        });
      } else {
        setContactForm({ ...emptyForm, ...defaultValues });
      }
      setError(null);
    }
  }, [isOpen, contact]);

  const handleSave = async () => {
    if (!contactForm.firstname || !contactForm.lastname) {
      setError("First name and last name are required");
      return;
    }

    const email = (contactForm.email || "").trim();
    if (!email) {
      setError(
        "Email is required — contacts use their email to sign in to the customer portal."
      );
      return;
    }
    // Catch obvious typos before the API rejects with a less friendly message.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const data = {
        password: generateRandomPassword(40),
        firstname: contactForm.firstname,
        lastname: contactForm.lastname,
        email: email,
        mobile: contactForm.mobile,
        title: contactForm.title || null,
        billing_group: contactForm.billing_group,
        technical_group: contactForm.technical_group,
        admin_group: contactForm.admin_group,
        account_id: accountId,
      };

      if (onSave) {
        await onSave(data, contact);
      }
    } catch (err) {
      console.error("Error saving contact:", err);
      // Surface the backend message (e.g. "Duplicate Email Found.") when it's a
      // plain string; validation errors come back as a field->messages object,
      // so fall back to a generic message for those.
      const apiMessage =
        err?.response?.data?.message || err?.response?.data?.error_message;
      setError(
        typeof apiMessage === "string"
          ? apiMessage
          : "Failed to save contact. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="tw-relative tw-z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="tw-ease-out tw-duration-300"
          enterFrom="tw-opacity-0"
          enterTo="tw-opacity-100"
          leave="tw-ease-in tw-duration-200"
          leaveFrom="tw-opacity-100"
          leaveTo="tw-opacity-0"
        >
          <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50" />
        </Transition.Child>

        <div className="tw-fixed tw-inset-0 tw-overflow-y-auto">
          <div className="tw-flex tw-min-h-full tw-items-center tw-justify-center tw-p-4">
            <Transition.Child
              as={Fragment}
              enter="tw-ease-out tw-duration-300"
              enterFrom="tw-opacity-0 tw-scale-95"
              enterTo="tw-opacity-100 tw-scale-100"
              leave="tw-ease-in tw-duration-200"
              leaveFrom="tw-opacity-100 tw-scale-100"
              leaveTo="tw-opacity-0 tw-scale-95"
            >
              <Dialog.Panel className="tw-w-full tw-max-w-2xl tw-transform tw-overflow-hidden tw-rounded-2xl tw-bg-white dark:tw-bg-gray-800 tw-p-6 tw-shadow-xl tw-transition-all">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
                  <Dialog.Title className="tw-text-xl tw-font-bold tw-text-gray-900 dark:tw-text-white">
                    {contact ? "Edit Contact" : "Add Contact"}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="tw-p-1 tw-rounded-lg hover:tw-bg-gray-100 dark:hover:tw-bg-gray-700 tw-transition-colors"
                  >
                    <XMarkIcon className="tw-w-6 tw-h-6 tw-text-gray-500 dark:tw-text-gray-400" />
                  </button>
                </div>

                {error && (
                  <div className="tw-mb-4 tw-p-3 tw-rounded-lg tw-bg-red-50 dark:tw-bg-red-900/30 tw-text-sm tw-text-red-700 dark:tw-text-red-300">
                    {error}
                  </div>
                )}

                <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-mb-6">
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={contactForm.firstname}
                      onChange={(e) =>
                        setContactForm({
                          ...contactForm,
                          firstname: e.target.value,
                        })
                      }
                      className="tw-w-full tw-px-3 tw-py-2 tw-bg-white dark:tw-bg-gray-700 tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded-lg tw-text-sm tw-text-gray-900 dark:tw-text-white focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
                    />
                  </div>
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={contactForm.lastname}
                      onChange={(e) =>
                        setContactForm({
                          ...contactForm,
                          lastname: e.target.value,
                        })
                      }
                      className="tw-w-full tw-px-3 tw-py-2 tw-bg-white dark:tw-bg-gray-700 tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded-lg tw-text-sm tw-text-gray-900 dark:tw-text-white focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
                    />
                  </div>
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) =>
                        setContactForm({
                          ...contactForm,
                          email: e.target.value,
                        })
                      }
                      className="tw-w-full tw-px-3 tw-py-2 tw-bg-white dark:tw-bg-gray-700 tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded-lg tw-text-sm tw-text-gray-900 dark:tw-text-white focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
                    />
                    <p className="tw-mt-1 tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                      Used to sign in to the customer portal.
                    </p>
                  </div>
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      Mobile
                    </label>
                    <input
                      type="tel"
                      value={contactForm.mobile}
                      placeholder="+13055551212"
                      onChange={(e) =>
                        setContactForm({
                          ...contactForm,
                          mobile: e.target.value,
                        })
                      }
                      onBlur={(e) =>
                        setContactForm({
                          ...contactForm,
                          mobile: toE164(e.target.value),
                        })
                      }
                      className="tw-w-full tw-px-3 tw-py-2 tw-bg-white dark:tw-bg-gray-700 tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded-lg tw-text-sm tw-text-gray-900 dark:tw-text-white focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
                    />
                  </div>
                  <div className="tw-col-span-2">
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      value={contactForm.title}
                      onChange={(e) =>
                        setContactForm({
                          ...contactForm,
                          title: e.target.value,
                        })
                      }
                      className="tw-w-full tw-px-3 tw-py-2 tw-bg-white dark:tw-bg-gray-700 tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded-lg tw-text-sm tw-text-gray-900 dark:tw-text-white focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
                    />
                  </div>
                </div>

                <div className="tw-mb-6">
                  <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-3">
                    Contact Authorizations
                  </label>
                  <div className="tw-space-y-3">
                    <label className="tw-flex tw-items-start tw-gap-2">
                      <input
                        type="checkbox"
                        checked={contactForm.admin_group}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            admin_group: e.target.checked,
                          })
                        }
                        className="tw-rounded tw-border-gray-300 dark:tw-border-gray-600 tw-text-purple-600 focus:tw-ring-purple-500 tw-mt-0.5"
                      />
                      <div>
                        <span className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300">
                          Account Authorized
                        </span>
                        <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                          Authorized to manage the account and contact ITP on
                          all matters
                        </p>
                      </div>
                    </label>
                    <label className="tw-flex tw-items-start tw-gap-2">
                      <input
                        type="checkbox"
                        checked={contactForm.billing_group}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            billing_group: e.target.checked,
                          })
                        }
                        className="tw-rounded tw-border-gray-300 dark:tw-border-gray-600 tw-text-blue-600 focus:tw-ring-blue-500 tw-mt-0.5"
                      />
                      <div>
                        <span className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300">
                          Billing Authorized
                        </span>
                        <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                          Authorized to contact ITP regarding billing and
                          invoices
                        </p>
                      </div>
                    </label>
                    <label className="tw-flex tw-items-start tw-gap-2">
                      <input
                        type="checkbox"
                        checked={contactForm.technical_group}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            technical_group: e.target.checked,
                          })
                        }
                        className="tw-rounded tw-border-gray-300 dark:tw-border-gray-600 tw-text-green-600 focus:tw-ring-green-500 tw-mt-0.5"
                      />
                      <div>
                        <span className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300">
                          Technical Authorized
                        </span>
                        <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                          Authorized to approve technical changes and service
                          modifications
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                  <button
                    onClick={onClose}
                    className="tw-px-4 tw-py-2 tw-rounded-lg tw-bg-gray-100 dark:tw-bg-gray-700 hover:tw-bg-gray-200 dark:hover:tw-bg-gray-600 tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white tw-transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="tw-px-4 tw-py-2 tw-rounded-lg tw-bg-blue-500 hover:tw-bg-blue-600 tw-text-sm tw-font-semibold tw-text-white tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                  >
                    {saving
                      ? "Saving..."
                      : contact
                      ? "Update Contact"
                      : "Create Contact"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
